import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import type { EstadoGeneral } from '../../types/database.types';

interface FormState {
  nombre: string;
  documento_nit: string;
  telefono: string;
  whatsapp: string;
  email: string;
  direccion: string;
  observaciones: string;
  estado: EstadoGeneral;
}

const VACIO: FormState = {
  nombre: '',
  documento_nit: '',
  telefono: '',
  whatsapp: '',
  email: '',
  direccion: '',
  observaciones: '',
  estado: 'activo',
};

export function ProveedorForm() {
  const { id } = useParams<{ id: string }>();
  const esNuevo = id === 'nuevo';
  const navigate = useNavigate();
  const { perfil } = useAuth();

  const [form, setForm] = useState<FormState>(VACIO);
  const [loading, setLoading] = useState(!esNuevo);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (esNuevo || !id) return;
    supabase
      .from('proveedores')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error: err }) => {
        if (err || !data) {
          setError(err?.message ?? 'No se encontró el proveedor');
        } else {
          setForm({
            nombre: data.nombre,
            documento_nit: data.documento_nit ?? '',
            telefono: data.telefono ?? '',
            whatsapp: data.whatsapp ?? '',
            email: data.email ?? '',
            direccion: data.direccion ?? '',
            observaciones: data.observaciones ?? '',
            estado: data.estado,
          });
        }
        setLoading(false);
      });
  }, [esNuevo, id]);

  function actualizar<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    // useParams() tipa `id` como `string | undefined` aunque la ruta
    // siempre lo trae ('nuevo' o un uuid real) -- este guard se lo
    // confirma a TypeScript para que `.eq('id', id)` más abajo compile
    // con `id: string` en vez de `string | undefined`.
    if (!id) return;
    setGuardando(true);
    setError(null);

    const payload = {
      salon_id: perfil.salon_id,
      nombre: form.nombre,
      documento_nit: form.documento_nit || null,
      telefono: form.telefono || null,
      whatsapp: form.whatsapp || null,
      email: form.email || null,
      direccion: form.direccion || null,
      observaciones: form.observaciones || null,
      estado: form.estado,
    };

    const { error: err } = esNuevo
      ? await supabase.from('proveedores').insert(payload)
      : await supabase.from('proveedores').update(payload).eq('id', id);

    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/proveedores');
  }

  if (loading) return <p className="text-sm text-onyx/50">Cargando…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title={esNuevo ? 'Nuevo proveedor' : 'Editar proveedor'} />

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nombre" required>
            <input
              required
              className="input-field"
              value={form.nombre}
              onChange={(e) => actualizar('nombre', e.target.value)}
            />
          </Campo>
          <Campo label="NIT / Documento">
            <input
              className="input-field"
              value={form.documento_nit}
              onChange={(e) => actualizar('documento_nit', e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Teléfono">
            <input className="input-field" value={form.telefono} onChange={(e) => actualizar('telefono', e.target.value)} />
          </Campo>
          <Campo label="WhatsApp">
            <input className="input-field" value={form.whatsapp} onChange={(e) => actualizar('whatsapp', e.target.value)} />
          </Campo>
        </div>

        <Campo label="Correo electrónico">
          <input type="email" className="input-field" value={form.email} onChange={(e) => actualizar('email', e.target.value)} />
        </Campo>

        <Campo label="Dirección">
          <input className="input-field" value={form.direccion} onChange={(e) => actualizar('direccion', e.target.value)} />
        </Campo>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Observaciones">
            <input
              className="input-field"
              value={form.observaciones}
              onChange={(e) => actualizar('observaciones', e.target.value)}
            />
          </Campo>
          <Campo label="Estado">
            <select
              className="input-field"
              value={form.estado}
              onChange={(e) => actualizar('estado', e.target.value as EstadoGeneral)}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </Campo>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/proveedores')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}
